import { MATH_BLOCK_REGEX, MATH_INLINE_REGEX, legacyTiptapToMarkdown, unescapeMarkdown } from '@caliu/utils'

/**
 * Extract markdown from note content.
 * Content is almost always already markdown (canonical storage format).
 * Legacy Tiptap JSON is handled as a fallback by the shared
 * `legacyTiptapToMarkdown` (`@caliu/utils`, mirrored in Swift and Kotlin).
 */
export function getExportMarkdown(content: string): string {
  return legacyTiptapToMarkdown(content)
}

/**
 * Note mentions `[[id|label]]` / `[[id]]` render as their plain label in exports
 * (there is no note to link to in a standalone file).
 */
export function stripNoteMentions(text: string): string {
  return text.replace(/\[\[[^\]|]*\|([^\]]+)\]\]/g, '$1').replace(/\[\[([^\]]+)\]\]/g, '$1')
}

/**
 * Strip all markdown formatting to produce plain text for a `.txt` export.
 *
 * Deliberately NOT the shared `notePreviewText` pipeline, even though both
 * "strip markdown to plain text". A preview is a lossy teaser; an export is the
 * user's data. The preview pipeline drops the title line, caps the output at a
 * line limit, discards whole tables and image lines, turns `<br>` into a space,
 * collapses blank lines away, and rewrites task markers as ◉/○. Every one of
 * those is silent content loss in an exported file. This keeps the title,
 * every line, table rows, image alt text and paragraph breaks, and maps `<br>`
 * to a real newline.
 *
 * The one thing it shares is the trailing unescape — see `unescapeMarkdown`.
 * Mirrored 1:1 by `ContentPipeline.stripMarkdown` in Swift (CaliuExport):
 * change both or neither.
 */
export function getExportPlainText(markdown: string): string {
  if (!markdown) return ''

  const stripped = stripNoteMentions(markdown)
    .replace(/<br\s*\/?>/gi, '\n') // hard breaks (raw)
    .replace(/&lt;br\s*\/?&gt;/gi, '\n') // hard breaks (entity-escaped)
    .replace(/```[\s\S]*?```/gm, '') // code blocks
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(MATH_BLOCK_REGEX, (_m, latex: string) => latex.trim()) // display math → bare LaTeX
    .replace(MATH_INLINE_REGEX, '$1') // inline math → bare LaTeX
    .replace(/^#{1,6}\s+/gm, '') // headings
    .replace(/\*\*([^*]+)\*\*/g, '$1') // bold
    .replace(/__([^_]+)__/g, '$1') // bold alt
    .replace(/\*([^*]+)\*/g, '$1') // italic
    .replace(/_([^_]+)_/g, '$1') // italic alt
    .replace(/~~([^~]+)~~/g, '$1') // strikethrough
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1') // images (before links)
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/^[-*+] \[[ x]\] /gm, '') // task lists
    .replace(/^[-*+]\s+/gm, '') // unordered lists
    .replace(/^\d+\.\s+/gm, '') // ordered lists
    .replace(/^>\s+/gm, '') // blockquotes
    .replace(/^---+$/gm, '') // horizontal rules
    .replace(/\n{3,}/g, '\n\n') // collapse blank lines

  // LAST, after every block-prefix strip: unescaping `1\.` any earlier would
  // let the ordered-list rule eat the number, and `\#` / `\-` / `\>` would
  // become real heading / bullet / quote markers.
  return unescapeMarkdown(stripped).trim()
}
